import { metricKeys, type GameEvent, type GameStateSnapshot, type LiveRateSnapshot, type MetricKey, type SessionAggregate, type SessionRecord, type ValidatedObservation } from '@/domain/types'
import type { GamePreset } from '@/presets/types'
import { LIVE_EFFICIENCY_WINDOW_MS, LIVE_RESOURCE_WINDOW_MS, TrustedRateEstimator } from './trusted-rate-estimator'

type RateMetric = 'exp' | 'mesos' | 'hp' | 'mp'
type MetricObservation = 'ignored' | 'baseline' | 'contiguous' | 'discontinuous'
const HOUR_MS = 3_600_000
const MAX_TRUSTED_INTERVAL_MS: Readonly<Record<RateMetric, number>> = {
  exp: 15_000,
  mesos: 15_000,
  hp: 2_000,
  mp: 2_000,
}

interface MetricClock {
  sourceAt: number | null
  lastActiveAt: number | null
  observedDurationMs: number
}

const emptyCoverage = () => Object.fromEntries(metricKeys.map((key) => [key, 0])) as SessionAggregate['coverage']
const emptyClocks = (): Record<RateMetric, MetricClock> => ({
  exp: { sourceAt: null, lastActiveAt: null, observedDurationMs: 0 },
  mesos: { sourceAt: null, lastActiveAt: null, observedDurationMs: 0 },
  hp: { sourceAt: null, lastActiveAt: null, observedDurationMs: 0 },
  mp: { sourceAt: null, lastActiveAt: null, observedDurationMs: 0 },
})
const createLiveRateEstimators = () => ({
  expPerHour: new TrustedRateEstimator(LIVE_EFFICIENCY_WINDOW_MS),
  mesosPerHour: new TrustedRateEstimator(LIVE_EFFICIENCY_WINDOW_MS),
  hpConsumedPerHour: new TrustedRateEstimator(LIVE_RESOURCE_WINDOW_MS),
  hpRecoveredPerHour: new TrustedRateEstimator(LIVE_RESOURCE_WINDOW_MS),
  mpConsumedPerHour: new TrustedRateEstimator(LIVE_RESOURCE_WINDOW_MS),
  mpRecoveredPerHour: new TrustedRateEstimator(LIVE_RESOURCE_WINDOW_MS),
})

export class SessionEngine {
  private record: SessionRecord | null = null
  private pauseStartedAt: number | null = null
  private accumulatedPauseMs = 0
  private latestState: GameStateSnapshot | null = null
  private expBaseline: GameStateSnapshot | null = null
  private pendingExpEvents: GameEvent[] = []
  private expGained = 0
  private hasExpBaseline = false
  private mesosBaseline: number | null = null
  private mesosDelta = 0
  private hasMesosBaseline = false
  private mesosUnavailable = false
  private clocks = emptyClocks()
  private totals = { hpConsumed: 0, hpRecovered: 0, mpConsumed: 0, mpRecovered: 0 }
  private liveRateEstimators = createLiveRateEstimators()

  constructor(private readonly preset: GamePreset) {}

  start(profileId: string, now = Date.now(), initialState: GameStateSnapshot | null = null): SessionRecord {
    if (this.record && this.record.status !== 'ended') throw new Error('A record is already active')
    this.resetRuntime(now)
    const recordInitialState = initialState ? { ...initialState, coverage: emptyCoverage() } : null
    this.record = {
      id: crypto.randomUUID(),
      presetId: this.preset.id,
      presetVersion: this.preset.version,
      profileId,
      status: 'running',
      startedAt: now,
      activeDurationMs: 0,
      aggregate: this.emptyAggregate(),
      ...(recordInitialState ? { startState: recordInitialState, endState: recordInitialState } : {}),
    }
    if (recordInitialState) this.establishInitialState(recordInitialState)
    return this.update({}, now)
  }

  pause(now = Date.now()): SessionRecord {
    this.assertStatus('running')
    this.pauseStartedAt = now
    this.suspendLiveRateEstimators()
    this.expBaseline = null
    this.pendingExpEvents = []
    this.mesosBaseline = null
    this.breakClockSegments()
    return this.update({ status: 'paused', pausedAt: now }, now)
  }

  resume(now = Date.now()): SessionRecord {
    this.assertStatus('paused', 'interrupted')
    if (this.pauseStartedAt !== null) this.accumulatedPauseMs += Math.max(0, now - this.pauseStartedAt)
    this.pauseStartedAt = null
    this.resumeLiveRateEstimators()
    return this.update({ status: 'running', pausedAt: undefined }, now)
  }

  interrupt(now = Date.now()): SessionRecord {
    this.assertStatus('running')
    this.pauseStartedAt = now
    this.suspendLiveRateEstimators()
    this.expBaseline = null
    this.pendingExpEvents = []
    this.mesosBaseline = null
    this.breakClockSegments()
    return this.update({ status: 'interrupted', pausedAt: now }, now)
  }

  ingest(state: GameStateSnapshot, events: readonly GameEvent[], observation?: ValidatedObservation): SessionRecord {
    this.assertStatus('running')
    const activeAt = this.activeDuration(state.capturedAt)
    const levelUpEvents = events.filter((event) => event.type === 'level-up')
    this.pendingExpEvents.push(...levelUpEvents)

    if (observation && !observation.accepted.exp) this.breakExpSegment()
    else this.ingestExp(state, activeAt)
    if (observation && !observation.accepted.mesos) {
      if (observation.mesos.availability === 'unavailable') this.markMesosUnavailable()
      else this.breakMesosSegment()
    }
    else this.ingestMesos(state, activeAt)
    if (observation && !observation.accepted.hp) this.breakClockSegment('hp')
    else this.observeResource('hp', state, activeAt)
    if (observation && !observation.accepted.mp) this.breakClockSegment('mp')
    else this.observeResource('mp', state, activeAt)

    for (const event of events) {
      if (event.type === 'hp-consumed') this.totals.hpConsumed += event.amount ?? 0
      if (event.type === 'hp-recovered') this.totals.hpRecovered += event.amount ?? 0
      if (event.type === 'mp-consumed') this.totals.mpConsumed += event.amount ?? 0
      if (event.type === 'mp-recovered') this.totals.mpRecovered += event.amount ?? 0
    }
    this.recordLiveRatePoints()

    const startState = this.record!.startState ?? state
    this.latestState = state
    return this.update({ startState, endState: state }, state.capturedAt)
  }

  end(now = Date.now()): SessionRecord {
    this.assertStatus('running', 'paused', 'interrupted')
    if (this.pauseStartedAt !== null) this.accumulatedPauseMs += Math.max(0, now - this.pauseStartedAt)
    this.pauseStartedAt = null
    return this.update({ status: 'ended', endedAt: now, pausedAt: undefined }, now)
  }

  current(now = Date.now()): SessionRecord | null {
    if (!this.record) return null
    return this.update({}, now)
  }

  liveRates(now = Date.now()): LiveRateSnapshot {
    return {
      expPerHour: this.liveRateEstimators.expPerHour.snapshot(now),
      mesosPerHour: this.liveRateEstimators.mesosPerHour.snapshot(now),
      hpConsumedPerHour: this.liveRateEstimators.hpConsumedPerHour.snapshot(now),
      hpRecoveredPerHour: this.liveRateEstimators.hpRecoveredPerHour.snapshot(now),
      mpConsumedPerHour: this.liveRateEstimators.mpConsumedPerHour.snapshot(now),
      mpRecoveredPerHour: this.liveRateEstimators.mpRecoveredPerHour.snapshot(now),
    }
  }

  private resetRuntime(now: number): void {
    this.pauseStartedAt = null
    this.accumulatedPauseMs = 0
    this.latestState = null
    this.expBaseline = null
    this.pendingExpEvents = []
    this.expGained = 0
    this.hasExpBaseline = false
    this.mesosBaseline = null
    this.mesosDelta = 0
    this.hasMesosBaseline = false
    this.mesosUnavailable = false
    this.clocks = emptyClocks()
    this.totals = { hpConsumed: 0, hpRecovered: 0, mpConsumed: 0, mpRecovered: 0 }
    this.resetLiveRateEstimators(now)
  }

  private establishInitialState(state: GameStateSnapshot): void {
    this.latestState = state
    if (state.level !== null && state.exp.absolute !== null) {
      this.expBaseline = state
      this.hasExpBaseline = true
      this.primeClock('exp', this.observedAt(state, 'exp'))
    }
    if (state.mesos !== null) {
      this.mesosBaseline = state.mesos
      this.hasMesosBaseline = true
      this.primeClock('mesos', this.observedAt(state, 'mesos'))
    }
    if (state.hp.current !== null || state.hp.ratio !== null) this.primeClock('hp', this.observedAt(state, 'hp'))
    if (state.mp.current !== null || state.mp.ratio !== null) this.primeClock('mp', this.observedAt(state, 'mp'))
    this.recordLiveRatePoints()
  }

  private ingestExp(state: GameStateSnapshot, activeAt: number): void {
    if (state.level === null || state.exp.absolute === null) return
    const sourceAt = this.observedAt(state, 'exp')
    const observation = this.observeMetric('exp', sourceAt, activeAt)
    if (observation === 'ignored' && this.pendingExpEvents.length === 0) return
    if (observation === 'baseline' || observation === 'discontinuous') {
      if (observation === 'discontinuous') this.markLiveRateDiscontinuity('exp')
      this.hasExpBaseline = true
      this.expBaseline = state
      this.pendingExpEvents = []
      return
    }

    if (this.expBaseline) {
      const delta = this.calculateExpDelta(this.expBaseline, state, this.pendingExpEvents)
      if (delta !== null) {
        this.expGained += delta
        this.expBaseline = state
        this.pendingExpEvents = []
      } else if (observation === 'contiguous' && this.expBaseline.level === state.level && state.exp.absolute >= this.expBaseline.exp.absolute!) {
        this.expBaseline = state
        this.pendingExpEvents = []
      }
    } else {
      this.hasExpBaseline = true
      this.expBaseline = state
      this.pendingExpEvents = []
    }
  }

  private ingestMesos(state: GameStateSnapshot, activeAt: number): void {
    if (state.mesos === null) return
    const sourceAt = this.observedAt(state, 'mesos')
    const observation = this.observeMetric('mesos', sourceAt, activeAt)
    if (observation === 'ignored') return
    if (this.mesosUnavailable) {
      this.liveRateEstimators.mesosPerHour.resumeFromUnavailable()
      this.mesosUnavailable = false
    }
    if (observation === 'baseline' || observation === 'discontinuous') {
      if (observation === 'discontinuous') this.markLiveRateDiscontinuity('mesos')
      this.mesosBaseline = state.mesos
      this.hasMesosBaseline = true
      return
    }

    if (this.mesosBaseline !== null) this.mesosDelta += state.mesos - this.mesosBaseline
    this.mesosBaseline = state.mesos
    this.hasMesosBaseline = true
  }

  private observeResource(metric: 'hp' | 'mp', state: GameStateSnapshot, activeAt: number): MetricObservation {
    const value = state[metric]
    if (value.current === null && value.ratio === null) return 'ignored'
    const observation = this.observeMetric(metric, this.observedAt(state, metric), activeAt)
    if (observation === 'discontinuous') this.markLiveRateDiscontinuity(metric)
    return observation
  }

  private observedAt(state: GameStateSnapshot, metric: MetricKey): number | null {
    const observed = state.observedAt?.[metric]
    return observed === undefined ? state.capturedAt : observed
  }

  private primeClock(metric: RateMetric, sourceAt: number | null): void {
    if (sourceAt === null) return
    this.clocks[metric] = { sourceAt, lastActiveAt: 0, observedDurationMs: 0 }
  }

  private observeMetric(metric: RateMetric, sourceAt: number | null, activeAt: number): MetricObservation {
    if (sourceAt === null) return 'ignored'
    const previous = this.clocks[metric]
    if (previous.sourceAt !== null && sourceAt <= previous.sourceAt) return 'ignored'
    const hasBaseline = previous.sourceAt !== null && previous.lastActiveAt !== null
    const sourceIntervalMs = previous.sourceAt === null ? 0 : sourceAt - previous.sourceAt
    const contiguous = hasBaseline && sourceIntervalMs <= MAX_TRUSTED_INTERVAL_MS[metric]
    const intervalMs = contiguous ? Math.max(0, activeAt - previous.lastActiveAt!) : 0
    this.clocks[metric] = {
      sourceAt,
      lastActiveAt: activeAt,
      observedDurationMs: previous.observedDurationMs + intervalMs,
    }
    if (!hasBaseline) return 'baseline'
    return contiguous ? 'contiguous' : 'discontinuous'
  }

  private breakClockSegments(): void {
    for (const metric of Object.keys(this.clocks) as RateMetric[]) {
      this.breakClockSegment(metric)
    }
  }

  private breakClockSegment(metric: RateMetric): void {
    if (this.clocks[metric].lastActiveAt !== null) this.markLiveRateDiscontinuity(metric)
    this.clocks[metric] = { ...this.clocks[metric], lastActiveAt: null }
  }

  private breakExpSegment(): void {
    this.expBaseline = null
    this.pendingExpEvents = []
    this.breakClockSegment('exp')
  }

  private breakMesosSegment(): void {
    this.mesosBaseline = null
    this.breakClockSegment('mesos')
  }

  private markMesosUnavailable(): void {
    this.mesosBaseline = null
    this.mesosUnavailable = true
    this.clocks.mesos = { ...this.clocks.mesos, lastActiveAt: null }
    this.liveRateEstimators.mesosPerHour.markUnavailable()
  }

  private resetLiveRateEstimators(now: number): void {
    this.liveRateEstimators = createLiveRateEstimators()
    for (const estimator of Object.values(this.liveRateEstimators)) estimator.reset(now)
  }

  private suspendLiveRateEstimators(): void {
    for (const estimator of Object.values(this.liveRateEstimators)) estimator.suspend()
  }

  private resumeLiveRateEstimators(): void {
    for (const estimator of Object.values(this.liveRateEstimators)) estimator.resume()
  }

  private markLiveRateDiscontinuity(metric: RateMetric): void {
    if (metric === 'exp') this.liveRateEstimators.expPerHour.markDiscontinuity()
    if (metric === 'mesos') this.liveRateEstimators.mesosPerHour.markDiscontinuity()
    if (metric === 'hp') {
      this.liveRateEstimators.hpConsumedPerHour.markDiscontinuity()
      this.liveRateEstimators.hpRecoveredPerHour.markDiscontinuity()
    }
    if (metric === 'mp') {
      this.liveRateEstimators.mpConsumedPerHour.markDiscontinuity()
      this.liveRateEstimators.mpRecoveredPerHour.markDiscontinuity()
    }
  }

  private recordLiveRatePoints(): void {
    const observe = (
      estimator: TrustedRateEstimator,
      metric: RateMetric,
      cumulativeTotal: number,
    ) => {
      const clock = this.clocks[metric]
      if (clock.sourceAt === null) return
      estimator.observe({
        trustedAt: clock.observedDurationMs,
        cumulativeTotal,
        sourceAt: clock.sourceAt,
      })
    }

    observe(this.liveRateEstimators.expPerHour, 'exp', this.expGained)
    observe(this.liveRateEstimators.mesosPerHour, 'mesos', this.mesosDelta)
    observe(this.liveRateEstimators.hpConsumedPerHour, 'hp', this.totals.hpConsumed)
    observe(this.liveRateEstimators.hpRecoveredPerHour, 'hp', this.totals.hpRecovered)
    observe(this.liveRateEstimators.mpConsumedPerHour, 'mp', this.totals.mpConsumed)
    observe(this.liveRateEstimators.mpRecoveredPerHour, 'mp', this.totals.mpRecovered)
  }

  private update(patch: Partial<SessionRecord>, now: number): SessionRecord {
    if (!this.record) throw new Error('No active record')
    const activeDurationMs = this.activeDuration(now)
    const next = { ...this.record, ...patch, activeDurationMs }
    this.record = { ...next, aggregate: this.aggregate(next, activeDurationMs) }
    return this.record
  }

  private activeDuration(now: number): number {
    if (!this.record) return 0
    const currentPause = this.pauseStartedAt === null ? 0 : Math.max(0, now - this.pauseStartedAt)
    return Math.max(0, now - this.record.startedAt - this.accumulatedPauseMs - currentPause)
  }

  private aggregate(record: SessionRecord, durationMs: number): SessionAggregate {
    const end = this.latestState ?? record.endState
    const expGained = this.hasExpBaseline ? this.expGained : null
    const mesosDelta = this.hasMesosBaseline ? this.mesosDelta : null
    return {
      durationMs,
      expGained,
      expPerHour: this.cumulativeRate(this.expGained, 'exp', this.hasExpBaseline),
      mesosDelta,
      mesosPerHour: this.cumulativeRate(this.mesosDelta, 'mesos', this.hasMesosBaseline),
      hpConsumed: this.totals.hpConsumed,
      hpConsumedPerHour: this.cumulativeRate(this.totals.hpConsumed, 'hp', this.clocks.hp.sourceAt !== null),
      hpRecovered: this.totals.hpRecovered,
      hpRecoveredPerHour: this.cumulativeRate(this.totals.hpRecovered, 'hp', this.clocks.hp.sourceAt !== null),
      mpConsumed: this.totals.mpConsumed,
      mpConsumedPerHour: this.cumulativeRate(this.totals.mpConsumed, 'mp', this.clocks.mp.sourceAt !== null),
      mpRecovered: this.totals.mpRecovered,
      mpRecoveredPerHour: this.cumulativeRate(this.totals.mpRecovered, 'mp', this.clocks.mp.sourceAt !== null),
      coverage: end?.coverage ?? emptyCoverage(),
    }
  }

  private emptyAggregate(): SessionAggregate {
    return {
      durationMs: 0,
      expGained: null,
      expPerHour: null,
      mesosDelta: null,
      mesosPerHour: null,
      hpConsumed: 0,
      hpConsumedPerHour: null,
      hpRecovered: 0,
      hpRecoveredPerHour: null,
      mpConsumed: 0,
      mpConsumedPerHour: null,
      mpRecovered: 0,
      mpRecoveredPerHour: null,
      coverage: emptyCoverage(),
    }
  }

  private cumulativeRate(total: number, metric: RateMetric, hasBaseline: boolean): number | null {
    if (!hasBaseline) return null
    const durationMs = this.clocks[metric].observedDurationMs
    if (durationMs <= 0) return 0
    return total / durationMs * HOUR_MS
  }

  private calculateExpDelta(previous: GameStateSnapshot, current: GameStateSnapshot, events: readonly GameEvent[]): number | null {
    if (previous.level === null || current.level === null || previous.exp.absolute === null || current.exp.absolute === null) return null
    if (previous.level === current.level) {
      const required = this.preset.experienceToNext(current.level)
      const usesEstimate = previous.exp.estimated || current.exp.estimated
      const delta = usesEstimate && required !== null && previous.exp.ratio !== null && current.exp.ratio !== null
        ? Math.round((current.exp.ratio - previous.exp.ratio) * required)
        : current.exp.absolute - previous.exp.absolute
      return delta >= 0 ? delta : null
    }
    const confirmedLevelUp = events.some((event) => event.type === 'level-up' && event.from === previous.level && event.to === current.level)
    if (!confirmedLevelUp || current.level !== previous.level + 1) return null
    const required = this.preset.experienceToNext(previous.level)
    if (required === null || previous.exp.absolute > required) return null
    return required - previous.exp.absolute + current.exp.absolute
  }

  private assertStatus(...allowed: SessionRecord['status'][]): void {
    if (!this.record || !allowed.includes(this.record.status)) throw new Error(`Invalid record transition from ${this.record?.status ?? 'none'}`)
  }
}
